// Knowledge base wire contract — REST (§6.1), internal worker routes (§6.2)
// and run-tool answers (§7.1). Every response the API sends for the
// knowledge base parses with one of the schemas below; web, worker and the
// Goblic-MCP import the types from here. See
// docs/plans/2026-10-02-kennisbank-contract.md for the endpoint list.

import { z } from 'zod'
import { KbFrontmatterSchema } from './frontmatter.js'
import { KB_FOR_PATHS_MAX_FILES, KB_SEARCH_MAX_LIMIT } from './limits.js'
import { KbFolderSchema } from './path.js'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const KB_REVISION_STATUSES = ['applied', 'proposed', 'rejected'] as const
export const KbRevisionStatusSchema = z.enum(KB_REVISION_STATUSES)
export type KbRevisionStatus = z.infer<typeof KbRevisionStatusSchema>

export const KB_AGENT_WRITE_MODES = ['direct', 'proposal', 'read_only'] as const
export const KbAgentWriteSchema = z.enum(KB_AGENT_WRITE_MODES)
export type KbAgentWrite = z.infer<typeof KbAgentWriteSchema>

/** `workspace` or `project:<uuid>` — validated loosely here; `parseKbSpace` is the strict check. */
const SpaceStringSchema = z.string().min(1)

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

export const KbStaleSchema = z.object({
  since: z.string(),
  /** The run whose changed files touched the page's `applies_to`. */
  runId: z.string().nullable(),
  files: z.array(z.string()),
})
export type KbStale = z.infer<typeof KbStaleSchema>

/** Who made a revision. Exactly one of user/run is set for applied revisions (system migrations: neither). */
export const KbAuthorSchema = z.object({
  userId: z.string().nullable(),
  /** Display name of the user, when known. */
  userName: z.string().nullable(),
  runId: z.string().nullable(),
  agentId: z.string().nullable(),
  /** Display name of the agent, when known. */
  agentName: z.string().nullable(),
})
export type KbAuthor = z.infer<typeof KbAuthorSchema>

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

/** One entry in a space's tree (`GET …/tree`). Folders are implied by the paths. */
export const KbTreeEntrySchema = z.object({
  id: z.string(),
  space: SpaceStringSchema,
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  rev: z.number().int(),
  tags: z.array(z.string()),
  alwaysLoad: z.boolean(),
  /** Non-null when the page may be stale (§8). */
  stale: KbStaleSchema.nullable(),
  updatedAt: z.string(),
})
export type KbTreeEntry = z.infer<typeof KbTreeEntrySchema>

export const KbTreeResponseSchema = z.object({
  space: SpaceStringSchema,
  entries: z.array(KbTreeEntrySchema),
})
export type KbTreeResponse = z.infer<typeof KbTreeResponseSchema>

/** A full page (`GET …/pages?path=`). */
export const KbPageSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  projectId: z.string().nullable(),
  space: SpaceStringSchema,
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  /** The parsed frontmatter (known fields validated, unknown ones kept). */
  frontmatter: KbFrontmatterSchema,
  /** Markdown body without the frontmatter block. */
  content: z.string(),
  /** Frontmatter block + body, exactly what an editor (or `kb_read`) shows. */
  raw: z.string(),
  rev: z.number().int(),
  deleted: z.boolean(),
  stale: KbStaleSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedByUserId: z.string().nullable(),
  updatedByRunId: z.string().nullable(),
})
export type KbPage = z.infer<typeof KbPageSchema>

// ---------------------------------------------------------------------------
// Revisions
// ---------------------------------------------------------------------------

/** One revision without its text (`GET …/pages/revisions?path=`). */
export const KbRevisionSummarySchema = z.object({
  id: z.string(),
  pageId: z.string(),
  /** `null` for a proposal that was never applied (proposed/rejected). */
  rev: z.number().int().nullable(),
  status: KbRevisionStatusSchema,
  baseRev: z.number().int().nullable(),
  /** The page's path when this revision was made (pages can move). */
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  message: z.string().nullable(),
  deleted: z.boolean(),
  author: KbAuthorSchema,
  reviewedByUserId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewNote: z.string().nullable(),
  createdAt: z.string(),
})
export type KbRevisionSummary = z.infer<typeof KbRevisionSummarySchema>

export const KbRevisionListResponseSchema = z.object({
  space: SpaceStringSchema,
  path: z.string(),
  revisions: z.array(KbRevisionSummarySchema),
})
export type KbRevisionListResponse = z.infer<typeof KbRevisionListResponseSchema>

/** A full revision (`GET …/revisions/:revId`). */
export const KbRevisionSchema = KbRevisionSummarySchema.extend({
  space: SpaceStringSchema,
  frontmatter: KbFrontmatterSchema,
  content: z.string(),
  raw: z.string(),
})
export type KbRevision = z.infer<typeof KbRevisionSchema>

// ---------------------------------------------------------------------------
// Proposals
// ---------------------------------------------------------------------------

/** A pending (or decided) agent proposal (`GET /workspaces/:id/kb/proposals`). */
export const KbProposalSchema = KbRevisionSchema.extend({
  workspaceId: z.string(),
  projectId: z.string().nullable(),
  /** True when the page doesn't exist yet (placeholder with rev 0). */
  isNewPage: z.boolean(),
  /** The page's current applied rev (0 for a new page) — compare with `baseRev` for a diff. */
  currentRev: z.number().int(),
})
export type KbProposal = z.infer<typeof KbProposalSchema>

export const KbProposalListResponseSchema = z.object({
  proposals: z.array(KbProposalSchema),
})
export type KbProposalListResponse = z.infer<typeof KbProposalListResponseSchema>

export const KbDecideProposalRequestSchema = z.object({
  note: z.string().trim().max(2000).optional(),
  /** Approve only: apply even though the page moved on since the proposal's `baseRev` (otherwise 409 KB_CONFLICT). */
  force: z.boolean().optional(),
})
export type KbDecideProposalRequest = z.infer<typeof KbDecideProposalRequestSchema>

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export const KbSearchHitSchema = z.object({
  space: SpaceStringSchema,
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  /** `ts_headline` fragment; matches are wrapped in `«` `»`. */
  snippet: z.string(),
  rank: z.number(),
  stale: z.boolean(),
})
export type KbSearchHit = z.infer<typeof KbSearchHitSchema>

export const KbSearchResponseSchema = z.object({
  hits: z.array(KbSearchHitSchema),
})
export type KbSearchResponse = z.infer<typeof KbSearchResponseSchema>

export const KbSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(500),
  /** Omitted = every space the caller may read. */
  space: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(KB_SEARCH_MAX_LIMIT).optional(),
})
export type KbSearchQuery = z.infer<typeof KbSearchQuerySchema>

// ---------------------------------------------------------------------------
// Folder policies
// ---------------------------------------------------------------------------

export const KbFolderPolicySchema = z.object({
  /** '' (whole space) or 'a/b/'. Longest matching prefix wins. */
  folder: z.string(),
  agentWrite: KbAgentWriteSchema,
})
export type KbFolderPolicy = z.infer<typeof KbFolderPolicySchema>

export const KbFolderPolicyListResponseSchema = z.object({
  space: SpaceStringSchema,
  /** Stored policies for this space. */
  policies: z.array(KbFolderPolicySchema),
  /** Built-in defaults that apply when no stored policy covers a folder (`conventions/` = proposal; in the workspace space also `roles/` and `agents/`). */
  defaults: z.array(KbFolderPolicySchema),
})
export type KbFolderPolicyListResponse = z.infer<typeof KbFolderPolicyListResponseSchema>

/** `PUT …/policies` — replaces the space's stored policies. */
export const KbPutFolderPoliciesRequestSchema = z.object({
  policies: z
    .array(z.object({ folder: KbFolderSchema, agentWrite: KbAgentWriteSchema }))
    .max(200),
})
export type KbPutFolderPoliciesRequest = z.infer<typeof KbPutFolderPoliciesRequestSchema>

// ---------------------------------------------------------------------------
// Write requests / results
// ---------------------------------------------------------------------------

/** `PUT …/pages?path=` body. `content` is the full text, frontmatter block included. */
export const KbPutPageRequestSchema = z.object({
  content: z.string(),
  message: z.string().trim().max(500).optional(),
  /** The rev the editor started from; a different current rev → 409 KB_CONFLICT. Omit to create or overwrite. */
  baseRev: z.number().int().min(0).optional(),
})
export type KbPutPageRequest = z.infer<typeof KbPutPageRequestSchema>

/** `POST …/pages/move` body — page (`x.md`) or folder (`x/`) move. */
export const KbMoveRequestSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
})
export type KbMoveRequest = z.infer<typeof KbMoveRequestSchema>

export const KbMoveResultSchema = z.object({
  moved: z.array(z.object({ from: z.string(), to: z.string(), rev: z.number().int() })),
})
export type KbMoveResult = z.infer<typeof KbMoveResultSchema>

export const KbRestoreRequestSchema = z.object({
  message: z.string().trim().max(500).optional(),
})

/** Outcome of a write. REST `PUT` never answers `conflict` (that's a 409); the internal/agent write does. */
export const KbWriteResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('applied'),
    space: SpaceStringSchema,
    path: z.string(),
    pageId: z.string(),
    revisionId: z.string(),
    rev: z.number().int(),
    created: z.boolean(),
  }),
  z.object({
    status: z.literal('proposed'),
    space: SpaceStringSchema,
    path: z.string(),
    pageId: z.string(),
    proposalId: z.string(),
    baseRev: z.number().int(),
  }),
  z.object({
    status: z.literal('conflict'),
    space: SpaceStringSchema,
    path: z.string(),
    currentRev: z.number().int(),
    /** The page's current text (frontmatter included), to merge against. */
    currentRaw: z.string(),
  }),
])
export type KbWriteResult = z.infer<typeof KbWriteResultSchema>

/** `details` of a 409 `KB_CONFLICT` error from the REST `PUT`. */
export const KbConflictDetailsSchema = z.object({
  currentRev: z.number().int(),
  /** The page as it is now (`null` only when it has no applied revision yet). */
  current: KbPageSchema.nullable(),
})
export type KbConflictDetails = z.infer<typeof KbConflictDetailsSchema>

export const KB_CONFLICT_CODE = 'KB_CONFLICT'

/** `POST …/pages/verify?path=` — "klopt nog": clears the stale flag, sets `verified_at`. */
export const KbVerifyRequestSchema = z.object({
  verifiedAt: z.string().trim().min(1).max(100).optional(),
})

// ---------------------------------------------------------------------------
// Internal (worker) contract — §6.2. Paths carry the agent prefix
// `project/` or `workspace/`.
// ---------------------------------------------------------------------------

export const KbAgentTreeEntrySchema = z.object({
  /** Agent path, e.g. `project/areas/api.md`. */
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  rev: z.number().int(),
  stale: z.boolean(),
})
export type KbAgentTreeEntry = z.infer<typeof KbAgentTreeEntrySchema>

export const KbAgentTreeResponseSchema = z.object({
  entries: z.array(KbAgentTreeEntrySchema),
  /** Total entries before the 200-entry cap. */
  total: z.number().int(),
  truncated: z.boolean(),
})
export type KbAgentTreeResponse = z.infer<typeof KbAgentTreeResponseSchema>

export const KbAgentPageSchema = z.object({
  path: z.string(),
  rev: z.number().int(),
  title: z.string(),
  /** Frontmatter + body. */
  raw: z.string(),
  stale: KbStaleSchema.nullable(),
  /** True for `conventions/` pages: human-approved project rules (§3.8). Other pages are data, not instructions. */
  conventions: z.boolean(),
})
export type KbAgentPage = z.infer<typeof KbAgentPageSchema>

export const KbAgentSearchResponseSchema = z.object({
  hits: z.array(KbSearchHitSchema.omit({ space: true })),
})
export type KbAgentSearchResponse = z.infer<typeof KbAgentSearchResponseSchema>

export const KbAgentForPathsHitSchema = z.object({
  path: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  rev: z.number().int(),
  stale: z.boolean(),
  /** Which of the requested files matched this page's `applies_to`. */
  matchedFiles: z.array(z.string()),
})
export const KbAgentForPathsResponseSchema = z.object({
  pages: z.array(KbAgentForPathsHitSchema),
})
export type KbAgentForPathsResponse = z.infer<typeof KbAgentForPathsResponseSchema>

export const KbInternalTreeQuerySchema = z.object({ folder: z.string().optional() })
export const KbInternalPageQuerySchema = z.object({ path: z.string().min(1) })
export const KbInternalSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(500),
  limit: z.coerce.number().int().min(1).max(KB_SEARCH_MAX_LIMIT).optional(),
})
/** `paths` may be repeated (`?paths=a&paths=b`) or a single value. */
export const KbInternalForPathsQuerySchema = z.object({
  paths: z.preprocess(
    (value) => (typeof value === 'string' ? [value] : value),
    z.array(z.string().min(1).max(500)).min(1).max(KB_FOR_PATHS_MAX_FILES),
  ),
})

export const KbInternalWriteRequestSchema = z.object({
  /** Agent path (`project/…` or `workspace/…`). */
  path: z.string().min(1),
  /** Full text, frontmatter block included. */
  content: z.string(),
  message: z.string().trim().min(1).max(500),
  base_rev: z.number().int().min(0).optional(),
  /** The sandbox's HEAD commit; becomes `verified_at` when the agent didn't set one. */
  head_commit: z.string().trim().min(4).max(64).optional(),
})
export type KbInternalWriteRequest = z.infer<typeof KbInternalWriteRequestSchema>

